import { useSport } from '@/hooks/useSport'
import { useApi } from '@/hooks/useApi'
import { fetchHighlights, fetchNews } from '@/lib/api'
import { HighlightCard } from '@/components/HighlightCard'
import { NewsCard } from '@/components/NewsCard'
import { SectionHeader } from '@/components/ui/SectionHeader'
import { Skeleton } from '@/components/ui/Skeleton'

export default function HighlightsPage() {
  const { sport, sportLabel } = useSport()

  const highlights = useApi(
    () => fetchHighlights(sport).then(d => d.highlights || []),
    [sport],
  )

  const news = useApi(
    () => fetchNews(sport).then(d => d.news_articles || []),
    [sport],
  )

  const hasHighlights = (highlights.data || []).length > 0
  const hasNews = (news.data || []).length > 0
  const isEmpty = !highlights.loading && !news.loading && !hasHighlights && !hasNews

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-white">{sportLabel} Highlights</h1>
        <p className="text-sm text-slate-400 mt-1">Latest college soccer highlight videos and coverage</p>
      </div>

      {/* YouTube video highlights */}
      {highlights.loading ? (
        <section>
          <SectionHeader title="Video Highlights" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} variant="card" className="h-56" />
            ))}
          </div>
        </section>
      ) : hasHighlights ? (
        <section>
          <SectionHeader title="Video Highlights" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {(highlights.data || []).map((h) => (
              <HighlightCard key={h.id} highlight={h} />
            ))}
          </div>
        </section>
      ) : null}

      {/* News highlights from ESPN */}
      {news.loading ? (
        <section>
          <SectionHeader title="News Coverage" linkTo={`/${sport}/news`} linkLabel="All News" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} variant="card" className="h-64" />
            ))}
          </div>
        </section>
      ) : hasNews ? (
        <section>
          <SectionHeader title="News Coverage" linkTo={`/${sport}/news`} linkLabel="All News" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {(news.data || []).map((article) => (
              <NewsCard key={article.id} article={article} sport={sport} />
            ))}
          </div>
        </section>
      ) : null}

      {isEmpty && (
        <div className="card p-12 text-center">
          <p className="text-slate-400">No highlights available yet. Check back soon.</p>
        </div>
      )}
    </div>
  )
}
